(ns minorodata.core
  (:require [applied-science.js-interop :as j]
            [cljs.spec.test.alpha]
            [minorodata.app.posthog]
            [minorodata.app.api-config :refer [API_HOST POSTHOG_PROJECT_TOKEN POSTHOG_API_HOST ENVIRONMENT]]
            [minorodata.app.db :as app.db]
            [minorodata.app.events]
            [minorodata.app.featurebase :as featurebase]
            [minorodata.app.handlers :refer [dispatch dispatch-sync]]
            [minorodata.app.logger :as logger]
            [minorodata.app.router :as app.router]
            [minorodata.app.subs]
            [minorodata.components.errors.core :as c.errors]
            [minorodata.components.layout.core :as c.layout]
            [minorodata.components.snacks.core :as c.snacks]
            [minorodata.components.sql-console-modal.core :as c.sql-console-modal]
            [minorodata.components.ai-chat.core :as c.ai-chat]
            [minorodata.components.core :as c]
            [minorodata.components.tables.details :as c.t-details]
            [minorodata.components.transformations.unit-test :as c.unit-test]
            [minorodata.components.transformations.db-copy :as c.db-copy]
            [minorodata.components.transformations.modals :as c.transformations-modals]
            [minorodata.components.transformations.json-flattening :as c.json-flattening]
            [minorodata.components.help.shortcuts-modal :as c.help.shortcuts.modal]
            [mold-http.core :as mold.http]
            [re-posh.core :as re-posh]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [reagent.dom.client :as dom]))


(def development?
  ^boolean goog.DEBUG)


(def default-layout-params
  {:sidebar true})


(defn layout-params [route-name route-options]
  (merge {:route route-name}
         (if (= route-name :blank)
           {:sidebar false}
           default-layout-params)
         (dissoc route-options :name)))


(defn set-dark-mode-class-if-needed
  [dark-mode?]
  (if dark-mode?
    (when (not (js/document.body.classList.contains "dark"))
      (js/document.body.classList.add "dark"))
    (js/document.body.classList.remove "dark")))


(defn read-feature-flag [feature-flag]
  (if (some? feature-flag)
    (->> feature-flag
         (map (fn [flag]
                (if (string? flag)
                  @(re-posh/subscribe [:unleash/is-enabled flag])
                  @(re-posh/subscribe flag))))
         (every? true?))
    true))


(defn app-root []
  (let [ready?       (re-posh/subscribe [:app.subs/app-ready?])
        route        (re-posh/subscribe [:app-route/pull])
        render-error (re-posh/subscribe [:app.subs/render-error])
        dark-mode?   (re-posh/subscribe [:app/prop :app/dark-mode?])]
    (reagent/create-class
     {:display-name
      "app-root"

      :constructor
      (fn []
        (dispatch-sync [:app/add {:app/ready? @ready?}])
        (dispatch [:users.api/fetch-user-preferences]))

      :component-did-update
      (fn []
        (set-dark-mode-class-if-needed @dark-mode?))

      :component-did-catch
      (fn [_this e _info]
        (dispatch [:sentry/capture-exception e]))

      :get-derived-state-from-error
      (fn [error]
        (dispatch [:app.events/set-render-error error])
        #js {})

      :render
      (fn []
        (when @ready?
          (let [{:app-route/keys [name params query options]} @route

                {:keys [main permissions feature-flag] screen-params :params}
                (c/screen-components {:route-name name})
                route-data       {:route  name
                                  :params params
                                  :query  query}
                ai-enabled?      @(re-posh/subscribe [:unleash/is-enabled "fe.ai-chat"])
                layout-props     (merge (layout-params name options)
                                        screen-params
                                        (when ai-enabled? {:ai true}))
                enabled?         (read-feature-flag feature-flag)
                restricted?      (not @(re-posh/subscribe [:users.subs/has-permissions? permissions]))]
            [:<>
             (cond
               (not enabled?)
               [c.layout/main (dissoc layout-props :header :container)
                [c.errors/not-supported-page]]

               restricted?
               [c.layout/main (dissoc layout-props :header :container)
                [c.errors/restricted-page]]

               (some? @render-error)
               [c.layout/main layout-props
                [c.errors/error-page]]

               :else
               [c.layout/main layout-props
                [main route-data]])

             [:f> c.json-flattening/modal]
             [:f> c.unit-test/modal]
             [:f> c.db-copy/modal]
             [:f> c.sql-console-modal/console-modal]
             (when ai-enabled?
               [:f> c.ai-chat/ai-modal])
             [:f> c.t-details/modal]
             [:f> c.help.shortcuts.modal/modal]
             [c.transformations-modals/modals]


             [c.snacks/stack]
             [:f> featurebase/use-lifecycle]
             (when ai-enabled?
               [:f> c.ai-chat/ai-button])
             #_[c.warehouse/warning {:route @route}]])))})))

(defonce root (dom/create-root (j/call js/document :getElementById "app")))

(defn mount []
  (dom/render root (reagent/as-element [(fn [] [app-root])])))


(defn on-update []
  (app.router/init-routes!)
  (mount))


(defn get-auth-header []
  (let [current-user @(re-posh/subscribe [:user/pull])
        token        (:user/session-token current-user)]
    (when (not (empty? token))
      (str "Bearer " token))))


(defn ^:export init []
  (when development?
    (enable-console-print!)
    (cljs.spec.test.alpha/instrument)

    (println "dev mode")
    (logger/install-dev-logger!))

  (dispatch [:posthog/init
             {:project-token POSTHOG_PROJECT_TOKEN
              :api-host POSTHOG_API_HOST
              :environment ENVIRONMENT}])

  (when-not development?
    (dispatch [:sentry/init]))

  (dispatch [:app.events/preinitialize])

  (mold.http/configure
   {:api-host           API_HOST
    :unauthorized-event [:user.events/redirect-to-login-page]
    :get-auth-header    get-auth-header})

  (app.db/reset-conn!)
  (app.router/init-routes!)

  (mount))
